import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Typography, 
  CircularProgress, 
  Paper, 
  Grid, 
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import axiosInstance from '../utils/axiosConfig';

// Define the NavigatorApplication type based on your model
interface NavigatorApplication {
  _id: string;
  name: string;
  email: string;
  phone: string;
  instrument: string[];
  location: string;
  pronouns: string;
  linkToWork: string;
  webLink: string;
  residencyGoals: string;
  locationDates: string[];
  parentalDuties: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

const NavigatorApplicantDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [application, setApplication] = useState<NavigatorApplication | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchApplication = async () => {
      try {
        const response = await axiosInstance.get(`/api/applications/submissions/${id}`);
        const data = response.data;
        
        // Map field names from FormSchema to what we need
        const fieldMap: Record<string, string> = {};
        data.formSchema.fields.forEach((field: any) => {
          fieldMap[field.name] = field.label;
        });
        
        // Helper to find value by label text
        const findByLabel = (labelText: string) => {
          const fieldName = Object.keys(fieldMap).find(name => 
            fieldMap[name].toLowerCase().includes(labelText.toLowerCase())
          );
          return fieldName ? data.values[fieldName] : null;
        };
        
        // Transform to Navigator format
        const transformed: NavigatorApplication = {
          _id: data._id,
          name: `${data.values.firstName || ''} ${data.values.lastName || ''}`.trim(),
          email: data.values.email || '',
          phone: data.values.phone || '',
          instrument: [], // Navigator 2026 doesn't collect instruments
          location: [data.values.streetAddress1, data.values.city, data.values.county, data.values.postcode]
            .filter(Boolean)
            .join(', '),
          pronouns: data.values.pronouns || '',
          linkToWork: findByLabel('work sample 1') || findByLabel('video') || '',
          webLink: findByLabel('web/social link 1') || findByLabel('website') || '',
          residencyGoals: findByLabel('residency plans') || findByLabel('work on') || '',
          locationDates: findByLabel('selected centre') || findByLabel('time period') || [],
          parentalDuties: findByLabel('additional needs') || findByLabel('caring') || '',
          status: (data.status || 'pending') as 'pending' | 'approved' | 'rejected',
          createdAt: data.submittedAt
        };
        
        setApplication(transformed);
      } catch (err: any) {
        setError(err.message || 'An error occurred while fetching the application.');
      } finally {
        setLoading(false);
      }
    };

    fetchApplication();
  }, [id]);

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (!application) {
    return <Typography>No application data found.</Typography>;
  }

  return (
    <Paper elevation={3} sx={{ padding: 3, marginTop: 3 }}>
      <Typography variant="h4" gutterBottom>Navigator Application Details</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="h6">Applicant Information</Typography>
          <List>
            <ListItem>
              <ListItemText primary="Name" secondary={application.name} />
            </ListItem>
            <ListItem>
              <ListItemText primary="Email" secondary={application.email} />
            </ListItem>
            <ListItem>
              <ListItemText primary="Phone" secondary={application.phone} />
            </ListItem>
            <ListItem>
              <ListItemText primary="Location" secondary={application.location} />
            </ListItem>
            {application.pronouns && (
              <ListItem>
                <ListItemText primary="Pronouns" secondary={application.pronouns} />
              </ListItem>
            )}
          </List>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="h6">Application Details</Typography>
          <List>
            {application.linkToWork && (
              <ListItem>
                <ListItemText 
                  primary="Link to Work" 
                  secondary={<a href={application.linkToWork} target="_blank" rel="noopener noreferrer">{application.linkToWork}</a>} 
                />
              </ListItem>
            )}
            {application.webLink && (
              <ListItem>
                <ListItemText 
                  primary="Web Link" 
                  secondary={<a href={application.webLink} target="_blank" rel="noopener noreferrer">{application.webLink}</a>} 
                />
              </ListItem>
            )}
          </List>
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6">Residency Goals</Typography>
          <Typography 
            paragraph 
            sx={{ whiteSpace: 'pre-line' }}
          >
            {application.residencyGoals}
          </Typography>
        </Grid>
        {application.locationDates && application.locationDates.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="h6">Location Preferences</Typography>
            <List>
              {application.locationDates.map((location, index) => (
                <ListItem key={index}>
                  <ListItemText primary={location} />
                </ListItem>
              ))}
            </List>
          </Grid>
        )}
        {application.parentalDuties && (
          <Grid item xs={12}>
            <Typography variant="h6">Additional Needs / Caring Responsibilities</Typography>
            <Typography 
              paragraph
              sx={{ whiteSpace: 'pre-line' }}
            >
              {application.parentalDuties}
            </Typography>
          </Grid>
        )}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6">Application Status</Typography>
          <Chip 
            label={application.status.toUpperCase()} 
            color={application.status === 'approved' ? 'success' : application.status === 'rejected' ? 'error' : 'default'}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            Application submitted on: {new Date(application.createdAt).toLocaleString()}
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default NavigatorApplicantDetailPage;